import { message, Spin } from "antd";
import axios, { AxiosError } from "axios";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { getCurrentTSDataApi, getHistoricalTSDataApi } from "../axios/portalData";
import MachineCard from "../Components/MachineCard/machineCard";
import { MachineDocument, MachinesByGroup } from "../Types/dashboardTypes";
import NoMachines from "./noMachines";
import DashboardSecondaryHeader from "../Components/DashboardSecondaryHeader.tsx/dashboardSecondaryHeader";
import OEECard from "../Components/OEECard/OEECard";
import OEEGraphCard from "../Components/OEEGraphCard/OEEGraphCard";
import MachineDataContext, { MachineDataType } from "../themeContext/MachineDataContext";
import { HistoricalTsiDataType } from "../Types/portalTypes";
import UserContext from "../themeContext/UserContext";
import GroupDocumentContext from "../themeContext/GroupDocumentContext";
import MachineDocumentContext from "../themeContext/MachineDocumentContext";
import SelectedGroupContext from "../themeContext/selectedGroupContext";

interface SerialNames {
  machineSerial: string;
  machineName: string;
}

const Dashboard: React.FC = () => {
  const [hasMachines, setHasMachines] = useState(false);
  const { selectedGroup, setSelectedGroup } = useContext(SelectedGroupContext);
  const [machinesByGroup, setMachinesByGroup] = useState<MachinesByGroup[]>([]);
  const [currentMachineIndex, setCurrentMachineIndex] = useState(0);
  const [allCurrentTsiData, setAllCurrentTsiData] = useState<MachineDataType>({});
  const [allHistoricalTsiData, setAllHistoricalTsiData] = useState<HistoricalTsiDataType>();
  const [groupHistoricalData, setGroupHistoricalData] = useState<HistoricalTsiDataType>({});
  const [isLoading, setIsLoading] = useState(true);
  const { userInfo } = useContext(UserContext);
  const accountType = userInfo.accountType;
  const { groupDocument } = useContext(GroupDocumentContext);
  const { machineDocuments } = useContext(MachineDocumentContext);

  // get the CURRENT TSI data
  useEffect(() => {
    machineDocuments && tsiApiTimeout(machineDocuments, setAllCurrentTsiData);
    const interval = setInterval(() => {
      tsiApiTimeout(machineDocuments ? machineDocuments : [], setAllCurrentTsiData);
    }, 15000);
    return () => clearInterval(interval);
  }, [machineDocuments]);

  // get historical TSI data
  useEffect(() => {
    if (machineDocuments && machineDocuments.length > 0) {
      const machineIds: string[] = [];
      machineDocuments.map((value: { serialNumber: string }) => {
        machineIds.push(value.serialNumber);
      });
      const apiCounter = 1;
      const retry = (machineIds: string[], apiCounter: number) => {
        getHistoricalTSDataApi(machineIds)
          .then((res) => {
            setAllHistoricalTsiData(res);
          })
          .catch((error: Error | AxiosError) => {
            console.log("Counter: " + apiCounter);
            if (apiCounter < 3) {
              apiCounter += 1;
              retry(machineIds, apiCounter);
            } else {
              if (axios.isAxiosError(error)) {
                console.log("AxiosError message: ", error);
                message.error(error.message);
              } else {
                console.log("unexpected error: ", error);
                message.error("An unexpected error occurred");
              }
            }
          });
      };
      retry(machineIds, apiCounter);
    }
  }, [machineDocuments]);

  // when a group changes in the UI, set the new historical group data
  useEffect(() => {
    if (allHistoricalTsiData !== undefined) {
      const groupData: HistoricalTsiDataType = {};
      machinesByGroup.map((value) => {
        groupData[value.machineSerial] = allHistoricalTsiData[value.machineSerial] ?? {};
      });
      setGroupHistoricalData(groupData);
    }
  }, [allHistoricalTsiData, selectedGroup, machinesByGroup]);

  // When user selects a group, this will set the state for all the machines to
  // be associated with this group
  useEffect(() => {
    if (machineDocuments && machineDocuments.length > 0 && groupDocument.groupNames.length > 0) {
      const serialNames: SerialNames[] = [];
      const names: string[] = [];
      machineDocuments.map((value: MachineDocument) => {
        if (groupDocument.groupDocument.groups[selectedGroup as string].includes(value.serialNumber)) {
          serialNames.push({
            machineSerial: value.serialNumber,
            machineName: value.machineName,
          });
          names.push(value.machineName);
        }
      });
      setMachinesByGroup(serialNames);
      setCurrentMachineIndex(0);
      setHasMachines(true);
      setIsLoading(false);
    } else {
      machineDocuments && setIsLoading(false);
    }
  }, [machineDocuments, groupDocument, selectedGroup]);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }
  return (
    <div>
      {hasMachines ? (
        <>
          <MachineDataContext.Provider value={allCurrentTsiData}>
            <div className="dashboard-main">
              <DashboardSecondaryHeader
                groupHistoricalData={groupHistoricalData}
                groupDocument={groupDocument}
                selectedGroup={selectedGroup as string}
                setSelectedGroup={setSelectedGroup as Dispatch<SetStateAction<string>>}
              />
              <div className={accountType !== "free" ? "dashboard-main-3-cards not-free" : "dashboard-main-3-cards"}>
                <div className="dashboard-row-1">
                  <OEECard machinesByGroup={machinesByGroup} groupHistoricalData={groupHistoricalData} />
                  <OEEGraphCard machinesByGroup={machinesByGroup} groupHistoricalData={groupHistoricalData} />
                </div>
                <MachineCard
                  groupHistoricalData={groupHistoricalData}
                  machinesByGroup={machinesByGroup}
                  currentMachineIndex={currentMachineIndex}
                  setCurrentMachineIndex={setCurrentMachineIndex}
                />
              </div>
            </div>
          </MachineDataContext.Provider>
        </>
      ) : (
        <NoMachines />
      )}
    </div>
  );
};

export default Dashboard;

//--------------------------------------------------------------------------
//--------------------------------------------------------------------------

function tsiApiTimeout(
  machineDocuments: MachineDocument[],
  setAllCurrentTsiData: React.Dispatch<React.SetStateAction<MachineDataType>>
) {
  if (machineDocuments.length > 0) {
    const apiCounter = 1;
    const machineIds: string[] = [];
    machineDocuments.map((value) => {
      machineIds.push(value.serialNumber);
    });
    const retry = (machineIds: string[], apiCounter: number) => {
      getCurrentTSDataApi(machineIds)
        .then((res) => {
          setAllCurrentTsiData(res);
        })
        .catch((error: Error | AxiosError) => {
          console.log("Counter: " + apiCounter);
          if (apiCounter < 3) {
            apiCounter += 1;
            retry(machineIds, apiCounter);
          } else {
            if (axios.isAxiosError(error)) {
              console.log("AxiosError message: ", error);
              message.error(error.message);
            } else {
              console.log("unexpected error: ", error);
              message.error("An unexpected error occurred");
            }
          }
        });
    };
    retry(machineIds, apiCounter);
  }
}
